import type { RootState } from '@/store';
import { type PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { enqueueReport } from './reportBatcher';
import type { ReportRequest, ReportRow } from './reportingV2.api';

/**
 * Generic canonical reporting state. The page fires one report query per widget
 * (KPIs, channel donut, geo, …); each result is stored under a string `key` so
 * any number of widgets share this slice without bespoke state per widget.
 */
export interface WidgetState {
  rows: ReportRow[];
  /** Size of the whole result set, not just `rows` — the grid pager needs it. */
  totalRows: number;
  loading: boolean;
  error: string | null;
  // Latest in-flight request id for this key. Batched requests can have several
  // in flight, so we drop completions that aren't the most recent dispatch —
  // otherwise a slow earlier request could overwrite newer rows.
  reqId?: string;
  /**
   * Filter keys the API dropped because the source table this query had to
   * read carries no such column (flank#4880) — e.g. device_ids/publishers on a
   * geo-dimensioned query, which GEOGRAPHY has neither of. Empty when nothing
   * was dropped. Worth surfacing: the rows come back looking filtered while
   * being unfiltered on that key.
   */
  ignoredFilters: string[];
  /**
   * Metric/dimension/filter keys the caller isn't permitted to see (e.g.
   * `metrics.client_spend`), dropped server-side before the query ran. Empty
   * when nothing was restricted. Lets a widget render "restricted" distinctly
   * from "genuinely blank" instead of the same dash for both.
   */
  restrictedFields: string[];
  /**
   * Field keys dropped for a reason other than "you can't see this" (e.g. the
   * `ribeye_publisher` rollup's capped raw-bundle fetch hit its row cap,
   * flank#4643) — "unavailable for this view", not "not shown to you". Same
   * "present only when non-empty" convention as `restrictedFields`.
   */
  truncatedFields: string[];
}

export type ReportingV2Grouping = 'all' | 'dsp' | 'channel' | 'product';

export interface ReportingV2Filters {
  dateStart: string;
  dateEnd: string;
  datePreset: string;
  dsps: string[];
  advertiserIds: string[];
  /** Ribeye campaign ids (#4614) — id strings, as the options endpoint returns them. */
  ribeyeCampaignIds: string[];
  /** Ribeye line-item ids (#4614). */
  ribeyeLineItemIds: string[];
  productIds: string[];
  publishers: string[];
  channelIds: string[];
  deviceIds: string[];
  whitelabelIds: string[];
  clientIds: string[];
  grouping: ReportingV2Grouping;
}

export interface ReportingV2State {
  filters: ReportingV2Filters;
  widgets: Record<string, WidgetState>;
  freshness: Record<string, string>;
  /**
   * True while the Impressions by Type grid is on its Publishers inventory
   * chip (flank#4643), which fetches its own rows via `usePublisherInventory`
   * instead of the generic `impressionsByType` widget — so that widget's
   * store entry sits unfetched (or stale from whatever chip was active
   * before). ExportDialog has no other way to know this: it reads widgets
   * from this same global slice but the active chip is local state in
   * ImpressionsByType.tsx (review, Lucas — a global export reading a stale
   * per-chip widget otherwise silently exports the wrong chip's rows, or
   * nothing at all, under the Impressions by Type section's own name).
   */
  publisherInventoryActive: boolean;
  /**
   * Impressions by Type rows with live DV360 reach applied (flank#4479), or null when live
   * reach isn't active. ExportDialog reads widget rows from this slice, so without this it
   * would export the summed canonical reach the screen replaced.
   */
  impressionsByTypeLiveRows: ReportRow[] | null;
  /** True while those live values are still being fetched, so exports wait like they do for widgets. */
  impressionsByTypeLiveLoading: boolean;
  /**
   * Current-period reach/frequency from DV360 live reach for the KPI Compare
   * table (flank#4479), or null when live reach isn't active/covering this
   * row. ExportDialog's Key Metrics section otherwise reads the `totals`
   * widget's pre-live row and silently exports the summed canonical reach
   * the screen replaced.
   */
  kpiCompareLiveReach: { reach: number | null; frequency: number | null } | null;
  /** True while that current-period live value is still being fetched (CodeRabbit, flank#5188: without this an export mid-fetch silently got the pre-live summed reach instead of waiting). */
  kpiCompareLiveLoading: boolean;
  /**
   * Impressions Over Time's per-day rows with live DV360 reach applied
   * (flank#4479) — only ever set while the frequency overlay is on, since
   * that's the only thing that fetches per-day live reach. Null otherwise,
   * same as `impressionsByTypeLiveRows` above.
   */
  trendLiveRows: ReportRow[] | null;
  /** True while those per-day live rows are still being fetched (CodeRabbit, flank#5188). */
  trendLiveLoading: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Inclusive last-N-days window ending today (local time). */
export const lastNDays = (n: number): { start: string; end: string } => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (n - 1));
  return { start: ymd(start), end: ymd(end) };
};

// Default to a 30-day window rather than the design's 7.
//
// The 7-day default assumed canonical carried data through the latest ingest for
// every DSP. That assumption breaks whenever ingest pauses: measured 2026-07-30,
// gam / dv360 / simplifi / adswizz had not ingested since 2026-07-24, so a
// trailing-7-day window returned zero rows and the page opened completely empty —
// which reads as a broken report rather than a stale pipeline. 30 days keeps the
// page meaningful across a normal ingest gap; the freshness line in the header is
// what communicates the actual recency.
export const DEFAULT_DATE_PRESET = 'Last 30 Days';
const DEFAULT_RANGE = lastNDays(30);

const initialState: ReportingV2State = {
  filters: {
    dateStart: DEFAULT_RANGE.start,
    dateEnd: DEFAULT_RANGE.end,
    datePreset: DEFAULT_DATE_PRESET,
    dsps: [],
    advertiserIds: [],
    ribeyeCampaignIds: [],
    ribeyeLineItemIds: [],
    productIds: [],
    publishers: [],
    channelIds: [],
    deviceIds: [],
    whitelabelIds: [],
    clientIds: [],
    // Open on DSP, not Product. Two reasons, and they agree:
    //
    // 1. Coverage. Measured 2026-07-30 over Jul 1-30: grouping by Product puts
    //    83% of impressions in "Unclassified" and by Channel 66%, because
    //    product_id / channel_id are still being populated. DSP_TYPE is present
    //    on every row, so By DSP is 0% unmapped — the only axis where the default
    //    view isn't mostly one grey bucket.
    // 2. It matches the agreed display logic from the 2026-07-28 Prod/Eng call:
    //    filter by DSP rather than channel for this iteration.
    grouping: 'dsp',
  },
  widgets: {},
  freshness: {},
  publisherInventoryActive: false,
  impressionsByTypeLiveRows: null,
  impressionsByTypeLiveLoading: false,
  kpiCompareLiveReach: null,
  kpiCompareLiveLoading: false,
  trendLiveRows: null,
  trendLiveLoading: false,
};

// Widgets keep dispatching one fetch each; `enqueueReport` coalesces every
// query fired in the same tick into a single /report-batch round trip.
export const fetchReportWidget = createAsyncThunk<
  {
    key: string;
    rows: ReportRow[];
    totalRows: number;
    freshness: Record<string, string>;
    ignoredFilters: string[];
    restrictedFields: string[];
    truncatedFields: string[];
  },
  { key: string; request: ReportRequest },
  { state: RootState }
>('reportingV2/fetchWidget', async ({ key, request }) => {
  const { rows, totalRows, freshness, ignoredFilters, restrictedFields, truncatedFields } = await enqueueReport(
    key,
    request,
  );
  return { key, rows, totalRows, freshness, ignoredFilters, restrictedFields, truncatedFields };
});

export const reportingV2Slice = createSlice({
  name: 'reportingV2',
  initialState,
  reducers: {
    setReportingV2Filters(state, { payload }: PayloadAction<Partial<ReportingV2Filters>>) {
      const next = { ...state.filters, ...payload };

      /**
       * Clear selections the new scope can no longer contain.
       *
       * Without this, Advertiser A -> Campaign C1 -> Advertiser B leaves
       * `ribeyeCampaignIds: ['C1']` in the request, so every widget returns zero
       * rows. The campaign dropdown has meanwhile refetched to B's campaigns, so
       * C1 isn't listed and there's no row to untick — the page is stuck empty
       * with only "Clear All" as an escape, and nothing explains why. White Label
       * and Client scope the ribeye options too (#4614), so they clear the same way.
       */
      const changed = (key: 'whitelabelIds' | 'clientIds' | 'advertiserIds' | 'ribeyeCampaignIds') =>
        payload[key] !== undefined && JSON.stringify(payload[key]) !== JSON.stringify(state.filters[key]);

      // A Client change also clears the Advertiser it scoped, the same stale-
      // selection bug the ribeye cascade below exists for, one tier up.
      if (changed('clientIds') && payload.advertiserIds === undefined) {
        next.advertiserIds = [];
      }
      if (changed('whitelabelIds') || changed('clientIds') || changed('advertiserIds')) {
        if (payload.ribeyeCampaignIds === undefined) next.ribeyeCampaignIds = [];
        if (payload.ribeyeLineItemIds === undefined) next.ribeyeLineItemIds = [];
      }
      if (changed('ribeyeCampaignIds') && payload.ribeyeLineItemIds === undefined) {
        next.ribeyeLineItemIds = [];
      }

      state.filters = next;
    },
    setPublisherInventoryActive(state, { payload }: PayloadAction<boolean>) {
      state.publisherInventoryActive = payload;
    },
    setImpressionsByTypeLive(state, { payload }: PayloadAction<{ rows: ReportRow[] | null; loading: boolean }>) {
      state.impressionsByTypeLiveRows = payload.rows;
      state.impressionsByTypeLiveLoading = payload.loading;
    },
    setKpiCompareLiveReach(
      state,
      {
        payload,
      }: PayloadAction<{ value: { reach: number | null; frequency: number | null } | null; loading: boolean }>,
    ) {
      state.kpiCompareLiveReach = payload.value;
      state.kpiCompareLiveLoading = payload.loading;
    },
    setTrendLiveRows(state, { payload }: PayloadAction<{ rows: ReportRow[] | null; loading: boolean }>) {
      state.trendLiveRows = payload.rows;
      state.trendLiveLoading = payload.loading;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchReportWidget.pending, (state, action) => {
        const prev = state.widgets[action.meta.arg.key];
        state.widgets[action.meta.arg.key] = {
          rows: prev?.rows ?? [],
          // Keep the previous total while reloading so the pager doesn't
          // collapse to "0 rows" for the duration of every page change.
          totalRows: prev?.totalRows ?? 0,
          loading: true,
          error: null,
          reqId: action.meta.requestId,
          ignoredFilters: prev?.ignoredFilters ?? [],
          restrictedFields: prev?.restrictedFields ?? [],
          truncatedFields: prev?.truncatedFields ?? [],
        };
      })
      .addCase(fetchReportWidget.fulfilled, (state, action) => {
        const { key, rows, totalRows, freshness, ignoredFilters, restrictedFields, truncatedFields } = action.payload;
        // Drop stale completions: only the most recent dispatch for this key wins.
        if (state.widgets[key]?.reqId !== action.meta.requestId) return;
        state.widgets[key] = {
          rows,
          totalRows,
          loading: false,
          error: null,
          reqId: action.meta.requestId,
          ignoredFilters,
          restrictedFields,
          truncatedFields,
        };
        if (freshness && Object.keys(freshness).length > 0) state.freshness = freshness;
      })
      .addCase(fetchReportWidget.rejected, (state, action) => {
        // Ignore aborts from rapid filter changes; surface real failures.
        if (action.error?.name === 'CanceledError') return;
        const prev = state.widgets[action.meta.arg.key];
        if (prev?.reqId !== action.meta.requestId) return;
        state.widgets[action.meta.arg.key] = {
          rows: [],
          totalRows: 0,
          loading: false,
          error: action.error?.message ?? 'Failed to load',
          reqId: action.meta.requestId,
          // Carry the last-known restrictions/truncation forward, same as
          // `pending` above — resetting to [] here made a restricted
          // permission flip open on any failed background refetch (review,
          // Lucas: #4650's fail-closed behavior regressed).
          ignoredFilters: prev?.ignoredFilters ?? [],
          restrictedFields: prev?.restrictedFields ?? [],
          truncatedFields: prev?.truncatedFields ?? [],
        };
      });
  },
});

export const {
  setReportingV2Filters,
  setPublisherInventoryActive,
  setImpressionsByTypeLive,
  setKpiCompareLiveReach,
  setTrendLiveRows,
} = reportingV2Slice.actions;
export default reportingV2Slice.reducer;

const EMPTY_WIDGET: WidgetState = {
  rows: [],
  totalRows: 0,
  loading: false,
  error: null,
  ignoredFilters: [],
  restrictedFields: [],
  truncatedFields: [],
};

export const selectReportingV2Filters = (state: RootState): ReportingV2Filters => state.reportingV2.filters;
export const selectPublisherInventoryActive = (state: RootState): boolean => state.reportingV2.publisherInventoryActive;
export const selectReportingV2Freshness = (state: RootState): Record<string, string> => state.reportingV2.freshness;
export const selectImpressionsByTypeLiveRows = (state: RootState): ReportRow[] | null =>
  state.reportingV2.impressionsByTypeLiveRows;
export const selectKpiCompareLiveReach = (
  state: RootState,
): { reach: number | null; frequency: number | null } | null => state.reportingV2.kpiCompareLiveReach;
export const selectTrendLiveRows = (state: RootState): ReportRow[] | null => state.reportingV2.trendLiveRows;
export const selectReportingV2Widget =
  (key: string) =>
  (state: RootState): WidgetState =>
    state.reportingV2.widgets[key] ?? EMPTY_WIDGET;

/** True while any widget query is in flight — drives the page-level progress bar. */
export const selectReportingV2AnyLoading = (state: RootState): boolean =>
  state.reportingV2.impressionsByTypeLiveLoading ||
  state.reportingV2.kpiCompareLiveLoading ||
  state.reportingV2.trendLiveLoading ||
  Object.values(state.reportingV2.widgets).some(widget => widget.loading);
